// IndexedDB-backed media cache untuk Web Player.
// Tujuan: konten tetap play meskipun WiFi mati / server down — standar
// digital signage komersial (Yodeck, Screenly, dll.).
//
// Schema:
//   db: "signage-player-cache" v1
//   store: "media"
//     - key: media.id (string UUID)
//     - value: { id, url, mime_type, size, blob, etag?, fetched_at, last_used_at }
//   store: "meta" (key-value misc; mis. terakhir manifest yang aktif)

import { openDB, type IDBPDatabase, type DBSchema } from "idb";

const DB_NAME = "signage-player-cache";
const DB_VERSION = 1;

interface CachedMedia {
  id: string;
  url: string;
  mime_type: string;
  size: number;
  blob: Blob;
  etag?: string;
  fetched_at: number; // epoch ms
  last_used_at: number;
}

interface SignageDb extends DBSchema {
  media: {
    key: string;
    value: CachedMedia;
    indexes: { "by-last-used": number };
  };
  meta: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<SignageDb>> | null = null;

function getDb(): Promise<IDBPDatabase<SignageDb>> {
  if (!dbPromise) {
    dbPromise = openDB<SignageDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("media")) {
          const store = db.createObjectStore("media", { keyPath: "id" });
          store.createIndex("by-last-used", "last_used_at");
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta");
        }
      },
    });
  }
  return dbPromise;
}

// =========================================================================
// Persistence request (minta browser jangan auto-evict)
// =========================================================================

export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function getStorageEstimate(): Promise<{
  usage: number;
  quota: number;
  usagePct: number;
} | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const est = await navigator.storage.estimate();
    const usage = est.usage ?? 0;
    const quota = est.quota ?? 0;
    return {
      usage,
      quota,
      usagePct: quota > 0 ? (usage / quota) * 100 : 0,
    };
  } catch {
    return null;
  }
}

// =========================================================================
// Cache CRUD
// =========================================================================

export async function hasCachedMedia(mediaId: string): Promise<boolean> {
  const db = await getDb();
  const key = await db.getKey("media", mediaId);
  return key !== undefined;
}

export async function getCachedMedia(mediaId: string): Promise<CachedMedia | null> {
  const db = await getDb();
  const cached = await db.get("media", mediaId);
  if (!cached) return null;
  // Touch last_used_at (in-place, async). Best-effort.
  void touchLastUsed(mediaId);
  return cached;
}

async function touchLastUsed(mediaId: string): Promise<void> {
  try {
    const db = await getDb();
    const item = await db.get("media", mediaId);
    if (item) {
      item.last_used_at = Date.now();
      await db.put("media", item);
    }
  } catch {
    // ignore
  }
}

export async function putCachedMedia(params: {
  id: string;
  url: string;
  mime_type: string;
  blob: Blob;
  etag?: string;
}): Promise<void> {
  const db = await getDb();
  const now = Date.now();
  await db.put("media", {
    id: params.id,
    url: params.url,
    mime_type: params.mime_type,
    size: params.blob.size,
    blob: params.blob,
    etag: params.etag,
    fetched_at: now,
    last_used_at: now,
  });
}

export async function deleteCachedMedia(mediaId: string): Promise<void> {
  const db = await getDb();
  await db.delete("media", mediaId);
}

export async function listCachedIds(): Promise<string[]> {
  const db = await getDb();
  return db.getAllKeys("media");
}

export async function getCacheSize(): Promise<{ count: number; bytes: number }> {
  const db = await getDb();
  const all = await db.getAll("media");
  return {
    count: all.length,
    bytes: all.reduce((sum, m) => sum + m.size, 0),
  };
}

// =========================================================================
// Eviction
// =========================================================================
//
// Sederhana: hapus media yang TIDAK ada di whitelist (current manifest)
// dan last_used_at lebih dari grace period (default 7 hari).
// Idenya: kalau user push manifest baru yang tidak include media lama,
// media lama masih disimpan beberapa hari (kalau user revert push).

const DEFAULT_EVICTION_GRACE_MS = 7 * 24 * 60 * 60 * 1000; // 7 hari

export async function evictUnused(params: {
  keepIds: Set<string>;
  graceMs?: number;
}): Promise<{ evicted: string[]; bytesFreed: number }> {
  const grace = params.graceMs ?? DEFAULT_EVICTION_GRACE_MS;
  const cutoff = Date.now() - grace;
  const db = await getDb();
  const all = await db.getAll("media");

  const evicted: string[] = [];
  let bytesFreed = 0;
  const tx = db.transaction("media", "readwrite");
  for (const item of all) {
    if (params.keepIds.has(item.id)) continue;
    if (item.last_used_at >= cutoff) continue;
    await tx.store.delete(item.id);
    evicted.push(item.id);
    bytesFreed += item.size;
  }
  await tx.done;
  return { evicted, bytesFreed };
}

// =========================================================================
// Meta storage (mis. last-applied manifest)
// =========================================================================

export async function setMeta<T>(key: string, value: T): Promise<void> {
  const db = await getDb();
  await db.put("meta", value, key);
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const db = await getDb();
  const v = await db.get("meta", key);
  return (v as T | undefined) ?? null;
}
